// docs/43 the cinematics' mid-scene beats: when the typed words reach a beat (core/story/cues.ts),
// its sound plays from bank `cue.<id>` (the variant picked by the beat's seed, so every screen hears
// the same take; a bed loops until the page turns) and its sight is drawn by <SightLayer>, a
// full-window overlay that sits over the scene and under the words. Shake and Rumble move the whole
// window. Reduced motion keeps the sounds and the light (flash, darken, glow) and drops the movement.
import { useEffect, useRef, type CSSProperties } from 'react';
import { create } from 'zustand';
import { playSfx, prepareSfx, sfxVariantCount, startLoop, stopLoop, type SfxKey } from '../../core/audio';
import { cueBank, type Beat, type Sight } from '../../core/story/cues';
import { motionReduced } from './Motion';
import './storyfx.css';

/** How long each sight stays on screen (ms); the CSS animations run to the same length. */
const LIFE: Record<Sight, number> = {
  FallingStar: 1800, Meteors: 3400, Lightning: 900, Flash: 700, Shake: 500, Rumble: 1800, Fireburst: 2400, Smoke: 3600,
  Sparks: 1600, Splash: 1800, Darken: 3200, Glow: 2800, Sparkle: 2800, Gust: 1800, Shadow: 2800, Eyes: 3400, Birds: 3200, Mist: 4200,
};
/** What reduced motion still draws: light, not movement. */
const STILL: ReadonlySet<Sight> = new Set(['Flash', 'Darken', 'Glow']);
const BED = 'story-bed';

interface Shown { id: number; sight: Sight; seed: number }
interface StoryFxStore { shown: Shown[]; bed?: string; push(s: Shown): void; drop(id: number): void; clear(): void }

let nextId = 1;
export const useStoryFx = create<StoryFxStore>()((set, get) => ({
  shown: [],
  push(s) { set({ shown: [...get().shown, s] }); },
  drop(id) { set({ shown: get().shown.filter((s) => s.id !== id) }); },
  clear() { if (get().bed) stopLoop(BED, 1200); set({ shown: [], bed: undefined }); },
}));

/**
 * The variant a beat plays: a bank is ordered best match first and has no cap on its size, so the
 * seed is squared toward the head (the best takes come up most) while every take can still play.
 */
export const headVariant = (seed: number, n: number): number => {
  const u = (seed % 100_003) / 100_003;
  return Math.min(n - 1, Math.floor(n * u * u));
};

/** Plays one beat now: its sound and its sight. */
export function fireBeat(b: Beat): void {
  if (b.sound) {
    const key = cueBank(b.sound) as SfxKey;
    const n = sfxVariantCount(key);
    if (n > 0 && b.loop) {
      const st = useStoryFx.getState();
      if (st.bed !== key) { if (st.bed) stopLoop(BED, 600); startLoop(BED, key, { gain: 0.7 }); useStoryFx.setState({ bed: key }); }
    } else if (n > 0) playSfx(key, { variant: headVariant(b.seed, n) });
    if (b.then) {
      const k2 = cueBank(b.then.sound) as SfxKey, n2 = sfxVariantCount(k2);
      if (n2 > 0) playSfx(k2, { variant: headVariant(b.seed >>> 3, n2), delayMs: b.then.delayMs });
    }
  }
  const sight = b.sight;
  if (!sight || (motionReduced() && !STILL.has(sight))) return;
  const id = nextId++;
  useStoryFx.getState().push({ id, sight, seed: b.seed });
  if (sight === 'Shake' || sight === 'Rumble') {
    const cls = `fk-fx-${sight.toLowerCase()}`;
    document.body.classList.remove(cls);
    void document.body.offsetWidth;   // restart the animation when two land close together
    document.body.classList.add(cls);
    window.setTimeout(() => document.body.classList.remove(cls), LIFE[sight]);
  }
  window.setTimeout(() => useStoryFx.getState().drop(id), LIFE[sight]);
}
// review hook: `__fkBeat({ at: 0, seed: 1, sight: 'FallingStar', sound: 'star_fall' })` in a dev build
if (import.meta.env.DEV || __FK_E2E__) (globalThis as unknown as Record<string, unknown>)['__fkBeat'] = fireBeat;

/**
 * Fires `beats` as `typed` (characters shown) passes them; `enabled` false (a page read before)
 * plays none. A line finished early lets what it skipped land a beat apart, in order. The beds
 * and sights end with `clear()` when the cinematic turns its page (`useBeatScope`).
 */
export function useBeats(beats: readonly Beat[], typed: number, enabled = true): void {
  // keyed by what the beats are, not the array: a re-render that rebuilds the story must not replay them
  const sig = beats.map((b) => `${b.at}:${b.sound ?? ''}:${b.sight ?? ''}:${b.seed}`).join('|');
  const latest = useRef(beats);
  latest.current = beats;
  const fired = useRef(0);
  useEffect(() => { fired.current = 0; }, [sig]);
  // the takes these beats will play, decoded while the page types (a bank has no cap on its size,
  // so it is never preloaded whole)
  useEffect(() => {
    if (!enabled) return;
    const warm = (sound: string, seed: number) => {
      const key = cueBank(sound) as SfxKey, n = sfxVariantCount(key);
      if (n > 0) prepareSfx(key, headVariant(seed, n));
    };
    for (const b of latest.current) {
      if (b.sound && !b.loop) warm(b.sound, b.seed);
      if (b.then) warm(b.then.sound, b.seed >>> 3);
    }
  }, [sig, enabled]);
  useEffect(() => {
    if (!enabled) return;
    const all = latest.current;
    const due: Beat[] = [];
    while (fired.current < all.length && all[fired.current]!.at <= typed) due.push(all[fired.current++]!);
    // a line finished early: what it skipped lands a beat apart, in story order (a text holds three at most)
    due.forEach((b, i) => (i ? window.setTimeout(() => fireBeat(b), 260 * i) : fireBeat(b)));
  }, [sig, typed, enabled]);
}

/** Clears the sights and the bed when `key` changes (the page turns) and when the cinematic ends. */
export function useBeatScope(key: unknown): void {
  useEffect(() => () => useStoryFx.getState().clear(), [key]);
}

function rand(seed: number) {
  let t = seed >>> 0;
  return () => { t = (t + 0x6d2b79f5) >>> 0; let x = Math.imul(t ^ (t >>> 15), t | 1); x ^= x + Math.imul(x ^ (x >>> 7), x | 61); return ((x ^ (x >>> 14)) >>> 0) / 4294967296; };
}
const v = (o: Record<string, string | number>) => o as CSSProperties;

/** A forked bolt from the top of the window to somewhere low on it. */
function bolt(r: () => number): string {
  let x = 30 + r() * 40, y = 0;
  const pts = [`${x},${y}`];
  while (y < 70) { y += 6 + r() * 9; x += (r() - 0.5) * 12; pts.push(`${x.toFixed(1)},${y.toFixed(1)}`); }
  return pts.join(' ');
}

function SightView({ sight, seed }: { sight: Sight; seed: number }) {
  const r = rand(seed);
  const cls = `fk-fx fk-fx--${sight.toLowerCase()}`;
  switch (sight) {
    case 'FallingStar':
      return <div className={cls}><span className="fk-fx__star" style={v({ '--x': `${20 + r() * 55}%`, '--y': `${6 + r() * 16}%`, '--a': `${18 + r() * 22}deg` })} /></div>;
    case 'Meteors':
      return <div className={cls}>{Array.from({ length: 7 }, (_, i) => <span key={i} className="fk-fx__star" style={v({ '--x': `${8 + r() * 80}%`, '--y': `${2 + r() * 26}%`, '--a': `${20 + r() * 14}deg`, animationDelay: `${(i * 0.38 + r() * 0.2).toFixed(2)}s` })} />)}</div>;
    case 'Lightning':
      return <div className={cls}><span className="fk-fx__white" /><svg className="fk-fx__bolt" viewBox="0 0 100 100" preserveAspectRatio="none"><polyline points={bolt(r)} /></svg></div>;
    case 'Flash':
      return <div className={cls}><span className="fk-fx__white" /></div>;
    case 'Shake':
      return null;
    case 'Rumble':
      return <div className={cls}>{Array.from({ length: 26 }, (_, i) => <span key={i} className="fk-fx__dust" style={v({ left: `${r() * 100}%`, animationDelay: `${(r() * 0.9).toFixed(2)}s`, '--s': `${2 + Math.round(r() * 2)}px` })} />)}</div>;
    case 'Fireburst':
      return <div className={cls}><span className="fk-fx__bloom" style={v({ '--x': `${35 + r() * 30}%` })} />{Array.from({ length: 18 }, (_, i) => <span key={i} className="fk-fx__ember" style={v({ left: `${30 + r() * 40}%`, '--dx': `${(r() - 0.5) * 160}px`, animationDelay: `${(r() * 0.6).toFixed(2)}s` })} />)}</div>;
    case 'Smoke':
      return <div className={cls}>{Array.from({ length: 9 }, (_, i) => <span key={i} className="fk-fx__puff" style={v({ left: `${25 + r() * 50}%`, '--dx': `${(r() - 0.5) * 120}px`, '--z': (1.4 + r() * 1.4).toFixed(2), animationDelay: `${(i * 0.18).toFixed(2)}s` })} />)}</div>;
    case 'Sparks': {
      const x = 30 + r() * 40, y = 45 + r() * 25;
      return <div className={cls} style={v({ '--x': `${x}%`, '--y': `${y}%` })}>{Array.from({ length: 24 }, (_, i) => { const a = r() * Math.PI * 2, d = 50 + r() * 140; return <span key={i} className="fk-fx__spark" style={v({ '--dx': `${(Math.cos(a) * d).toFixed(0)}px`, '--dy': `${(Math.sin(a) * d * 0.7 - 40).toFixed(0)}px` })} />; })}</div>;
    }
    case 'Splash': {
      const x = 30 + r() * 40;
      return <div className={cls} style={v({ '--x': `${x}%` })}>{[0, 1, 2].map((i) => <span key={i} className="fk-fx__ring" style={{ animationDelay: `${i * 0.28}s` }} />)}{Array.from({ length: 14 }, (_, i) => <span key={`d${i}`} className="fk-fx__drop" style={v({ '--dx': `${((r() - 0.5) * 180).toFixed(0)}px`, '--dy': `${(-60 - r() * 120).toFixed(0)}px` })} />)}</div>;
    }
    case 'Darken':
      return <div className={cls}><span className="fk-fx__dark" /></div>;
    case 'Glow':
      return <div className={cls}><span className="fk-fx__glow" style={v({ '--x': `${35 + r() * 30}%`, '--y': `${40 + r() * 25}%` })} /></div>;
    case 'Sparkle':
      return <div className={cls}>{Array.from({ length: 30 }, (_, i) => <span key={i} className="fk-fx__glint" style={v({ left: `${20 + r() * 60}%`, top: `${20 + r() * 55}%`, animationDelay: `${(r() * 1.6).toFixed(2)}s` })} />)}</div>;
    case 'Gust':
      return <div className={cls}>{Array.from({ length: 16 }, (_, i) => <span key={i} className={i % 3 ? 'fk-fx__streak' : 'fk-fx__leaf'} style={v({ top: `${10 + r() * 75}%`, animationDelay: `${(r() * 0.7).toFixed(2)}s`, '--w': `${60 + r() * 160}px` })} />)}</div>;
    case 'Shadow':
      return <div className={cls}><span className="fk-fx__shade" style={v({ '--y': `${r() * 20}%` })} /></div>;
    case 'Eyes':
      return <div className={cls}>{Array.from({ length: 3 }, (_, i) => <span key={i} className="fk-fx__eyes" style={v({ left: `${12 + i * 28 + r() * 14}%`, top: `${50 + r() * 22}%`, animationDelay: `${(i * 0.45).toFixed(2)}s` })}><i /><i /></span>)}</div>;
    case 'Birds':
      return <div className={cls}>{Array.from({ length: 14 }, (_, i) => <span key={i} className="fk-fx__bird" style={v({ left: `${25 + r() * 50}%`, top: `${34 + r() * 16}%`, '--dx': `${(r() - 0.3) * 520}px`, '--dy': `${-220 - r() * 220}px`, animationDelay: `${(r() * 0.5).toFixed(2)}s` })} />)}</div>;
    case 'Mist':
      return <div className={cls}><span className="fk-fx__fog fk-fx__fog--l" /><span className="fk-fx__fog fk-fx__fog--r" /></div>;
  }
}

/** The beats' sights, over the scene and under the words. `fixed` for a portal over the board. */
export function SightLayer({ fixed }: { fixed?: boolean }) {
  const shown = useStoryFx((s) => s.shown);
  return (
    <div className={`fk-fx-layer ${fixed ? 'fk-fx-layer--fixed' : ''}`} aria-hidden="true" data-testid="story-fx" data-sights={shown.map((s) => s.sight).join(' ')}>
      {shown.map((s) => <SightView key={s.id} sight={s.sight} seed={s.seed} />)}
    </div>
  );
}
